// ABOUTME: Chart 2 of FamilyInsightsPage — positivity ratio trend line chart over time
// ABOUTME: Issue #1607: shows Stars/(Triangles+Squares) per child across the selected time horizon

import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import naluV1Theme from '../theme/theme.nalu-v1';
import { Child } from '../types/api';
import { analyticsAPI } from '../services/api';
import { getChildColor } from '../utils/childColors';
import { getInsightsDateRange } from '../utils/dateUtils';

interface PositivityRatioDataPoint {
  period: string;
  starCount: number;
  negativeCount: number;
  // ratio is null when negativeCount=0; Recharts renders a gap via connectNulls={false}
  ratio: number | null;
  // isMaxPositive is true when ratio=null and starCount>0 (all-stars, no negatives)
  // The chart already handles this correctly via the null gap — isMaxPositive is not
  // consumed separately since the rendering behavior is the same either way.
  isMaxPositive: boolean;
}

interface ChildSeriesData {
  child: Child;
  data: PositivityRatioDataPoint[];
}

interface PositivityRatioTrendChartProps {
  familyId: number;
  childList: Child[];
  selectedChildId: number | 'all';
  activeTabLabel: string;
  familyTimezone: string | undefined;
  familyCreatedAt: string | undefined;
}

const PositivityRatioTrendChart: React.FC<PositivityRatioTrendChartProps> = ({
  familyId,
  childList,
  selectedChildId,
  activeTabLabel,
  familyTimezone,
  familyCreatedAt,
}) => {
  const theme = useTheme();
  // Compared by value (logoSrc), not object identity — dev-server HMR can give the
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // object references after a hot-reload even though both are "the nalu-v1 theme".
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [seriesData, setSeriesData] = useState<ChildSeriesData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const { startDate, endDate, granularity } = getInsightsDateRange(
          activeTabLabel,
          familyTimezone,
          familyCreatedAt
        );

        if (selectedChildId === 'all') {
          const results = await Promise.all(
            childList.map(async (child) => {
              const childId = child.id ?? child.childId ?? 0;
              const data = await analyticsAPI.getPositivityRatioOverTime(
                familyId,
                startDate,
                endDate,
                granularity,
                familyTimezone,
                childId
              );
              return { child, data };
            })
          );
          setSeriesData(results);
        } else {
          const data = await analyticsAPI.getPositivityRatioOverTime(
            familyId,
            startDate,
            endDate,
            granularity,
            familyTimezone,
            selectedChildId
          );
          const child = childList.find(c => (c.id ?? c.childId) === selectedChildId);
          setSeriesData(child ? [{ child, data }] : []);
        }
      } catch (err) {
        console.error('Failed to load positivity ratio data:', err);
        setError('Failed to load positivity ratio data');
      } finally {
        setLoading(false);
      }
    };

    if (familyId && childList.length > 0) {
      fetchData();
    }
  }, [familyId, childList, selectedChildId, activeTabLabel, familyTimezone, familyCreatedAt]);

  // Merge all series into a single dataset keyed by period for Recharts.
  // Pre-populate all periods in the date range so the X-axis shows a complete
  // timeline even when the API returns no data for some days.
  const chartData = useMemo(() => {
    const { startDate, endDate, granularity } = getInsightsDateRange(
      activeTabLabel,
      familyTimezone,
      familyCreatedAt
    );

    const periodMap = new Map<string, Record<string, number | null | string>>();

    // Pre-populate every period in the range with null for each child
    if (granularity === 'day') {
      const cursor = new Date(startDate + 'T12:00:00');
      const end = new Date(endDate + 'T12:00:00');
      while (cursor <= end) {
        const pad = (n: number) => n.toString().padStart(2, '0');
        const period = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}-${pad(cursor.getDate())}`;
        const entry: Record<string, number | null | string> = { period };
        seriesData.forEach(({ child }) => {
          entry[(child.id ?? child.childId ?? 0).toString()] = null;
        });
        periodMap.set(period, entry);
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    // Overlay actual API data
    seriesData.forEach(({ child, data }) => {
      const childId = (child.id ?? child.childId ?? 0).toString();
      data.forEach(point => {
        if (!periodMap.has(point.period)) {
          periodMap.set(point.period, { period: point.period });
        }
        periodMap.get(point.period)![childId] = point.ratio;
      });
    });

    return Array.from(periodMap.values()).sort((a, b) =>
      (a.period as string).localeCompare(b.period as string)
    );
  }, [seriesData, activeTabLabel, familyTimezone, familyCreatedAt]);

  // hasData reflects whether the API returned any actual data points (not just the pre-populated nulls)
  const hasData = seriesData.some(s => s.data.length > 0);

  const formatYAxisTick = (value: number): string => {
    if (value === 0) return '0:1';
    return `${Math.round(value)}:1`;
  };

  const formatXAxisTick = (period: string): string => {
    try {
      const date = new Date(period + 'T12:00:00'); // noon avoids timezone boundary issues
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return period;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant={isNaluV1Theme ? 'h4' : 'h6'} gutterBottom>
        Positivity Ratio
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress role="progressbar" />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {!loading && !error && !hasData && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <Typography color="text.secondary">
            No data available for this period. Log behaviors to see your positivity trend.
          </Typography>
        </Box>
      )}

      {!loading && !error && hasData && (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" tickFormatter={formatXAxisTick} fontSize={10} />
            <YAxis tickFormatter={formatYAxisTick} fontSize={10} />
            <Tooltip
              formatter={(value, name) => {
                const label = name ?? '';
                if (value == null || typeof value !== 'number') return ['\u221E:1', label];
                return [`${Math.round(value)}:1`, label];
              }}
            />
            {seriesData.length > 1 && <Legend />}
            {seriesData.map(({ child }) => {
              const childId = child.id ?? child.childId ?? 0;
              return (
                <Line
                  key={childId}
                  type="monotone"
                  dataKey={childId.toString()}
                  name={child.name}
                  stroke={getChildColor(childId)}
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default PositivityRatioTrendChart;
